import { Component, DestroyRef, inject, input, output, signal } from '@angular/core'
import type { OnInit } from '@angular/core'
import { CdkAriaLive } from '@angular/cdk/a11y'
import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import { NotificationContentComponent } from '../notification-content/notification-content.component'
import type { ToastEntry } from '../../services/toast-service/toast.service'
import { HapticService } from '../../services/haptic-service/haptic.service'

@Component({
  selector: 'aw-toast',
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  imports: [IconsDirective, CdkAriaLive, NotificationContentComponent],
  host: {
    '[class.notification]': 'entry().content.kind === "notification"',
    '[class.dragging]': 'pointer() !== null && hasDragged',
    '[class.leaving]': '!visible()',
    '[style.--toast-drag-y]': 'dragOffset() + "px"',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'onPointerEnd($event)',
    '(pointercancel)': 'onPointerEnd($event)',
    '(lostpointercapture)': 'onPointerEnd($event)',
    '(click)': 'activate($event)',
    '(keydown.enter)': 'onKeyboard($event)',
    '(keydown.space)': 'onKeyboard($event)'
  }
})
export class ToastComponent implements OnInit {
  readonly entry = input.required<ToastEntry>()
  readonly dismissed = output<void>()
  readonly visible = signal(true)
  protected readonly pointer = signal<PointerEvent | null>(null)
  protected readonly dragOffset = signal(0)
  protected readonly copied = signal(false)
  protected readonly categoryIcons = { warning: 'circle-alert', confirm: 'circle-check' }
  protected readonly copiedLabel = q.translations.common.copied
  private readonly haptics = inject(HapticService)
  protected hasDragged = false
  private timer?: ReturnType<typeof setTimeout>

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.timer)
      this.releasePointer()
    })
  }

  /** Starts the remaining display time and gives notification feedback once. */
  ngOnInit(): void {
    if (this.entry().content.kind === 'notification') {
      this.haptics.notification()
    }

    this.timer = setTimeout(() => this.dismiss(), Math.max(0, this.entry().hideAt - Date.now()))
  }

  /** Plays the exit before asking Angular to remove this entry. */
  dismiss(): void {
    if (this.visible()) {
      this.visible.set(false)
      this.releasePointer()
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.dismissed.emit(), 200)
    }
  }

  /**
   * Opens a notification or copies feedback, while rejecting drag-release clicks.
   * @param {Event} event Activation event.
   */
  activate(event: Event): void {
    if (!this.visible() || (this.hasDragged && event instanceof MouseEvent && event.detail > 0)) {
      event.preventDefault()

      return
    }

    const content = this.entry().content

    if (content.kind === 'notification') {
      this.dismiss()
      content.open()
    } else if (content.options.copyOnTap && navigator.clipboard) {
      void navigator.clipboard.writeText(content.message).then(() => this.copied.set(true))
    }
  }

  /**
   * Gives the copyable feedback surface keyboard activation; notification buttons use native clicks.
   * @param {Event} event Enter or Space event.
   */
  protected onKeyboard(event: Event): void {
    if (this.entry().content.kind === 'feedback') {
      event.preventDefault()
      this.activate(event)
    }
  }

  /**
   * Captures the pressed target so taps keep their native click target.
   * @param {PointerEvent} event Gesture origin.
   */
  protected onPointerDown(event: PointerEvent): void {
    if (this.visible() && event.isPrimary && event.button === 0 && !this.pointer()) {
      this.pointer.set(event)
      this.hasDragged = false

      const target = event.target as Element

      target.setPointerCapture(event.pointerId)
    }
  }

  /**
   * Tracks all drag directions for click suppression, but moves only upward.
   * @param {PointerEvent} event Pointer position.
   */
  protected onPointerMove(event: PointerEvent): void {
    const start = this.pointer()

    if (start?.pointerId === event.pointerId) {
      this.hasDragged ||= Math.hypot(event.clientX - start.clientX, event.clientY - start.clientY) > 8
      this.dragOffset.set(this.hasDragged ? Math.min(0, event.clientY - start.clientY) : 0)
    }
  }

  /**
   * Dismisses a completed upward swipe, or smoothly returns a missed or cancelled swipe.
   * @param {PointerEvent} event Release, cancellation, or capture-loss event.
   */
  protected onPointerEnd(event: PointerEvent): void {
    if (this.pointer()?.pointerId !== event.pointerId) {
      return
    }

    this.onPointerMove(event)
    this.releasePointer()

    if (event.type === 'pointerup' && this.dragOffset() <= -24) {
      this.dismiss()
    } else {
      this.dragOffset.set(0)
    }
  }

  /** Releases capture after clearing the gesture, so capture-loss cannot reset a completed swipe. */
  private releasePointer(): void {
    const event = this.pointer()

    this.pointer.set(null)

    if (event) {
      const target = event.target as Element

      if (target.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId)
      }
    }
  }
}
